import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import MatrixSignals from '../components/MatrixSignals';
import { UserActionsBar } from '../components/UserInteractionPlaceholders';
import RatingInput from '../components/RatingInput';
import CommentsSection from '../components/CommentsSection';
import { useLanguage } from '../context/LanguageContext';
import { reviewsApi, moviesApi, languageToTMDB } from '../services/api';
import { ArrowLeft, Clock, Calendar, User, Share2 } from 'lucide-react';

const ReviewPage = () => {
  const { id } = useParams();
  const { t, language } = useLanguage();
  const [review, setReview] = useState(null);
  const [movie, setMovie] = useState(null);
  const [similarMovies, setSimilarMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const tmdbLang = languageToTMDB[language] || 'en-US';

        // Fetch review
        const reviewData = await reviewsApi.getById(id);
        setReview(reviewData);

        // Fetch movie details
        const movieData = await moviesApi.getDetail(reviewData.tmdb_id, tmdbLang);
        setMovie(movieData);

        // Fetch similar movies
        const similarData = await moviesApi.getSimilar(reviewData.tmdb_id, 1, tmdbLang);
        setSimilarMovies(similarData.results.slice(0, 3));
      } catch (err) {
        console.error('Error fetching review:', err);
        setError('Review not found');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, language]);

  if (loading) {
    return (
      <div className="bg-[#0B0F0E] min-h-screen">
        <Header />
        <main className="pt-32 pb-20 flex items-center justify-center">
          <div className="w-12 h-12 border-2 border-[#52ea88] border-t-transparent animate-spin"></div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !review) {
    return (
      <div className="bg-[#0B0F0E] min-h-screen">
        <Header />
        <main className="pt-32 pb-20">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%] text-center">
            <h1 className="text-4xl font-semibold text-[#E6FFF6] mb-4">{t('reviews.reviewNotFound')}</h1>
            <Link to="/reviews" className="text-[#52ea88] hover:underline">
              {t('reviews.backToReviews')}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const genres = movie?.genres || [];

  return (
    <div className="bg-[#0B0F0E] min-h-screen">
      <Header />

      {/* Hero Banner */}
      <section className="relative h-[60vh] min-h-[500px] overflow-hidden">
        <div className="absolute inset-0">
          {movie?.backdrop ? (
            <img
              src={movie.backdrop}
              alt={movie.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-[#121917]" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F0E] via-[#0B0F0E]/70 to-[#0B0F0E]/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F0E]/80 to-transparent" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-[7.6923%] h-full flex items-end pb-12">
          <div className="grid lg:grid-cols-3 gap-8 w-full items-end">
            {/* Poster */}
            <div className="hidden lg:block">
              {movie?.poster && (
                <img
                  src={movie.poster}
                  alt={movie.title}
                  className="w-full max-w-[280px] border border-[#52ea88]/20 shadow-2xl"
                />
              )}
            </div>

            {/* Info */}
            <div className="lg:col-span-2 space-y-4">
              <Link
                to="/reviews"
                className="inline-flex items-center gap-2 text-[#52ea88] hover:text-[#E6FFF6] transition-colors text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('reviews.backToReviews')}
              </Link>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-[#E6FFF6] leading-tight">
                {review.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-[#9FBFB4]">
                {movie?.year && (
                  <span className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#52ea88]" />
                    {movie.year}
                  </span>
                )}
                {movie?.runtime && (
                  <span className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#52ea88]" />
                    {movie.runtime} min
                  </span>
                )}
                {movie?.director && (
                  <span className="flex items-center gap-2">
                    <User className="w-4 h-4 text-[#52ea88]" />
                    {movie.director}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                {genres.map((g) => (
                  <span
                    key={g.id}
                    className="px-3 py-1 border border-[#52ea88]/30 text-[#52ea88] text-sm"
                  >
                    {g.name}
                  </span>
                ))}
              </div>

              {/* User Actions */}
              <div className="flex items-center gap-4 pt-4">
                <UserActionsBar reviewId={id} />
                <button className="flex items-center gap-2 px-3 py-2 bg-[#121917] border border-[#52ea88]/10 text-[#9FBFB4] hover:border-[#52ea88]/30 hover:text-[#E6FFF6] transition-all">
                  <Share2 className="w-4 h-4" />
                  <span className="text-sm">{t('reviews.share')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Review Content */}
      <main className="py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%]">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* The Experience */}
              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-[#E6FFF6] flex items-center gap-3">
                  <span className="w-8 h-0.5 bg-[#52ea88]"></span>
                  {t('review.theExperience')}
                </h2>
                <p className="text-[#9FBFB4] text-lg leading-relaxed">
                  {review.the_experience}
                </p>
              </section>

              {/* Themes & Meaning */}
              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-[#E6FFF6] flex items-center gap-3">
                  <span className="w-8 h-0.5 bg-[#52ea88]"></span>
                  {t('review.themesAndMeaning')}
                </h2>
                <p className="text-[#9FBFB4] text-lg leading-relaxed">
                  {review.themes_and_meaning}
                </p>
              </section>

              {/* Direction & Performances */}
              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-[#E6FFF6] flex items-center gap-3">
                  <span className="w-8 h-0.5 bg-[#52ea88]"></span>
                  {t('review.directionAndPerformances')}
                </h2>
                <p className="text-[#9FBFB4] text-lg leading-relaxed">
                  {review.direction_and_performances}
                </p>
              </section>

              {/* Visual Language */}
              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-[#E6FFF6] flex items-center gap-3">
                  <span className="w-8 h-0.5 bg-[#52ea88]"></span>
                  {t('review.visualLanguage')}
                </h2>
                <p className="text-[#9FBFB4] text-lg leading-relaxed">
                  {review.visual_language}
                </p>
              </section>

              {/* Final Thoughts */}
              <section className="space-y-4 border-t border-[#52ea88]/10 pt-12">
                <h2 className="text-2xl font-semibold text-[#E6FFF6] flex items-center gap-3">
                  <span className="w-8 h-0.5 bg-[#52ea88]"></span>
                  {t('review.finalThoughts')}
                </h2>
                <p className="text-[#9FBFB4] text-lg leading-relaxed">
                  {review.final_thoughts}
                </p>
              </section>

              {/* Comments Section */}
              <section className="border-t border-[#52ea88]/10 pt-12">
                <CommentsSection reviewId={id} />
              </section>
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Matrix Signals */}
              <MatrixSignals signals={review.signals} />

              {/* User Rating */}
              <RatingInput reviewId={id} />

              {/* Movie Info Card */}
              <div className="p-6 bg-[#121917] border border-[#52ea88]/10 space-y-4">
                <h3 className="text-lg font-semibold text-[#E6FFF6]">{t('reviews.filmDetails')}</h3>
                <div className="space-y-3 text-sm">
                  {movie?.director && (
                    <div className="flex justify-between">
                      <span className="text-[#9FBFB4]">{t('reviews.directorLabel')}</span>
                      <span className="text-[#E6FFF6]">{movie.director}</span>
                    </div>
                  )}
                  {movie?.year && (
                    <div className="flex justify-between">
                      <span className="text-[#9FBFB4]">{t('reviews.yearLabel')}</span>
                      <span className="text-[#E6FFF6]">{movie.year}</span>
                    </div>
                  )}
                  {movie?.runtime && (
                    <div className="flex justify-between">
                      <span className="text-[#9FBFB4]">{t('reviews.runtimeLabel')}</span>
                      <span className="text-[#E6FFF6]">{movie.runtime} min</span>
                    </div>
                  )}
                  {genres.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-[#9FBFB4]">{t('reviews.genreLabel')}</span>
                      <span className="text-[#E6FFF6] text-right">{genres.map(g => g.name).join(', ')}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Similar Films */}
              {similarMovies.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-[#E6FFF6]">
                    {t('review.similarFilms')}
                  </h3>
                  <div className="space-y-3">
                    {similarMovies.map((m) => (
                      <Link
                        key={m.id}
                        to={`/movie/${m.id}`}
                        className="flex items-center gap-4 p-3 bg-[#121917] hover:bg-[#52ea88]/5 border border-[#52ea88]/10 hover:border-[#52ea88]/30 transition-all group"
                      >
                        {m.poster ? (
                          <img
                            src={m.poster}
                            alt={m.title}
                            className="w-12 h-18 object-cover"
                          />
                        ) : (
                          <div className="w-12 h-18 bg-[#0B0F0E] flex items-center justify-center">
                            <span className="text-[#9FBFB4]/50 text-xs">N/A</span>
                          </div>
                        )}
                        <div>
                          <h4 className="text-[#E6FFF6] group-hover:text-[#52ea88] transition-colors text-sm font-medium">
                            {m.title}
                          </h4>
                          <p className="text-[#9FBFB4] text-xs">{m.year}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ReviewPage;
